import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar } from '@/ui/Avatar';
import { DialogBody, DialogCancel, DialogFoot, DialogHead } from '@/ui/Dialog';
import { Icon } from '@/ui/Icon';
import { Callout, Chip, Empty, Loading, Segmented, Switch } from '@/ui/bits';
import { Popover, usePopover } from '@/ui/Popover';
import type { ListSendRequest } from '@/lib/actions';
import { ApiError, errorMessage } from '@/lib/api';
import { formatDayTime, formatNumber, plural } from '@/lib/format';
import { useMe } from '@/lib/hooks';
import { openDialog } from '@/lib/overlays';
import { toast, toastError } from '@/lib/toast';
import { durationLabel, firstName, intervalLabel, planListSend, type ListSendPreview, type SkippedPerson, type Template } from '@shared/mail';
import { FromRow } from './Composer';
import { cancelListSend, fetchListRoom, keys, previewListSend, startListSend } from './data';
import { Editor, FormatBar, MOD, type EditorHandle, type FormatBarHandle } from './Editor';
import { cleanPastedHtml, toEditorHtml } from './html';
import { escapeHtml, highlightMergeFields, isBlankHtml, limitSummary, listSentMessage, mergeFieldsIn, pacingBars, pacingNote, scheduleProblem, starterBody, withoutTitle } from './logic';
import { PickTime } from './Schedule';
import { MergeMenu, TemplateRow } from './Templates';
import { watchListSend } from './ListProgress';

// Send to a list: one personal copy per person, spaced out within the inbox's daily limit.

const TITLE = 'Send to a list';

export function openListSend(req: ListSendRequest): void {
  const ids = [...new Set(req.personIds)];
  openDialog({ label: TITLE, size: 'wide', render: (close) => <ListSendDialog ids={ids} listName={req.listName ?? null} close={close} /> });
}

const SKIP_TONE: Record<string, 'danger' | 'warning' | 'plain'> = { 'Do not email': 'danger', Unsubscribed: 'warning' };

function ListSendDialog({ ids, listName, close }: { ids: string[]; listName: string | null; close: () => void }) {
  const preview = useQuery({ queryKey: keys.listPreview(ids), queryFn: () => previewListSend(ids), enabled: ids.length > 0, staleTime: 30_000 });
  const sub = 'Everyone gets their own personal copy, spaced out so it lands like one-to-one mail.';

  if (ids.length === 0) {
    return (
      <>
        <DialogHead title={TITLE} onClose={close} />
        <DialogBody>
          <Empty icon="users" title="Nobody is selected">
            Select the people to email in People, then choose Email.
          </Empty>
        </DialogBody>
      </>
    );
  }
  if (!preview.data) {
    return (
      <>
        <DialogHead title={TITLE} sub={sub} onClose={close} />
        <DialogBody>
          {preview.isError ? (
            <Callout
              tone="danger"
              role="alert"
              title="The list is not loading"
              actions={
                <button type="button" className="btn btn-sm btn-tile" onClick={() => void preview.refetch()}>
                  <Icon name="refresh" />
                  Try again
                </button>
              }
            >
              {errorMessage(preview.error)}
            </Callout>
          ) : (
            <Loading label="Checking who can be emailed" />
          )}
        </DialogBody>
      </>
    );
  }
  return <ListSendForm ids={ids} listName={listName} sub={sub} data={preview.data} close={close} />;
}

type Field = 'subject' | 'body' | 'startAt' | 'from';

function ListSendForm({ ids, listName, sub, data, close }: { ids: string[]; listName: string | null; sub: string; data: ListSendPreview; close: () => void }) {
  const qc = useQueryClient();
  const me = useMe();
  const myFirst = firstName(me.user.name);
  const [mailboxId, setMailboxId] = useState(data.defaultMailboxId);
  const [subject, setSubject] = useState('');
  const starter = useMemo(() => starterBody('{first}', myFirst), [myFirst]);
  const [html, setHtml] = useState(starter);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [trackOpens, setTrackOpens] = useState(true);
  const [view, setView] = useState<'write' | 'preview'>('write');
  const [pi, setPi] = useState(0);
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [startAt, setStartAt] = useState<Date | null>(null);
  const [invalid, setInvalid] = useState<Partial<Record<Field, string>>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const editor = useRef<EditorHandle>(null);
  const fmt = useRef<FormatBarHandle>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const timePop = usePopover('dialog');

  const mb = data.mailboxes.find((m) => m.id === mailboxId) ?? null;
  const people = data.recipients;
  const count = people.length;
  const person = people[pi % Math.max(1, count)] ?? null;
  const values = person ? { first: firstName(person.name, person.email), company: person.company, myFirst } : null;
  const fields = mergeFieldsIn(`${subject}\n${html}`);

  const start = when === 'later' ? startAt : null;
  // What already counts against the limit around the start: the inbox's own numbers for now, or a fresh count for a later time.
  const room = useQuery({
    queryKey: keys.listRoom(mb?.id ?? '', start?.toISOString() ?? ''),
    queryFn: () => fetchListRoom(mb!.id, start!.toISOString()),
    enabled: !!mb && !!start,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  const counts = !mb ? null : !start ? { sentToday: mb.sentToday, waiting: mb.waiting } : (room.data ?? null);
  const taken = counts ? counts.sentToday + counts.waiting : null;
  const plan = useMemo(
    () => (mb && taken !== null ? planListSend({ start: start ?? new Date(), count, limit: mb.dailyLimit, usedToday: taken }) : null),
    [mb, taken, start, count],
  );
  const limit = mb && counts ? limitSummary(counts.sentToday, counts.waiting, count, mb.dailyLimit, !!start) : null;
  const bars = plan ? pacingBars(plan.times, plan.interval) : [];

  const clear = (f: Field) => {
    if (invalid[f]) setInvalid(({ [f]: _gone, ...rest }) => rest);
    setServerError(null);
  };

  const applyTemplate = (t: Template | null) => {
    if (!t) {
      setTemplateId(null);
      return;
    }
    const body = toEditorHtml(t.body);
    if (editor.current) editor.current.setHtml(body);
    setHtml(body);
    setSubject(t.subject);
    setTemplateId(t.id);
    clear('body');
    clear('subject');
  };

  const go = useMutation({
    mutationFn: () =>
      startListSend({ personIds: ids, listName, mailboxId: mailboxId!, subject: subject.trim(), html, text: '', templateId, trackOpens, startAt: start?.toISOString() ?? null }),
    onSuccess: (item) => {
      close();
      watchListSend(item.id);
      void qc.invalidateQueries({ queryKey: keys.running });
      toast(listSentMessage(item.total, item.intervalSeconds, start), {
        icon: start ? 'clock' : 'send',
        undo: () => {
          cancelListSend(item.id).then(
            (r) => {
              void qc.invalidateQueries({ queryKey: keys.running });
              toast(r.sent ? `List send cancelled. ${plural(r.sent, 'copy', 'copies')} already went out` : 'List send cancelled before the first email', { icon: 'undo' });
            },
            (err: unknown) => toastError(err),
          );
        },
      });
    },
    onError: (err) => {
      const f = err instanceof ApiError ? err.fields : {};
      setInvalid({ subject: f.subject, body: f.body, startAt: f.startAt, from: f.mailboxId ?? f.from });
      setServerError(errorMessage(err));
      if (f.subject) subjectRef.current?.focus();
    },
  });

  const submit = () => {
    if (go.isPending) return;
    setServerError(null);
    const fail = (f: Field, message: string) => {
      setInvalid({ [f]: message });
      setServerError(message);
    };
    if (count === 0) return fail('from', 'Nobody on this list can be emailed');
    if (!mb) return fail('from', 'Connect your own Gmail to send');
    if (!mb.canSend) return fail('from', mb.problem ?? 'This inbox cannot send');
    if (!subject.trim()) {
      subjectRef.current?.focus();
      return fail('subject', 'Add a subject');
    }
    if (isBlankHtml(html)) {
      setView('write');
      setTimeout(() => editor.current?.focus('start'), 0);
      return fail('body', 'Write a message');
    }
    if (when === 'later') {
      const late = scheduleProblem(startAt);
      if (late) return fail('startAt', late);
    }
    go.mutate();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  const used = counts?.sentToday ?? 0;
  const waiting = counts?.waiting ?? 0;
  const cap = Math.max(1, mb?.dailyLimit ?? 1);
  const pct = (n: number) => `${Math.min(100, (n / cap) * 100).toFixed(1)}%`;

  return (
    <div className="ls-dlg" onKeyDown={onKeyDown}>
      <DialogHead title={TITLE} sub={sub} onClose={close} />
      <DialogBody>
        <div className="ls-grid">
          <div className="ls-main">
            {serverError ? (
              <Callout tone="danger" role="alert" title="The list send did not start" className="ls-err">
                {serverError}
              </Callout>
            ) : null}
            {mb && !mb.canSend && !invalid.from ? (
              <Callout tone="warn" title={mb.kind === 'personal' ? 'Connect your own Gmail to send' : `${mb.address} cannot send`} className="ls-err">
                {mb.kind === 'personal' ? withoutTitle(mb.problem, 'Connect your own Gmail to send') : mb.problem}
              </Callout>
            ) : null}
            {!mb && !invalid.from ? (
              <Callout tone="warn" title="Connect your own Gmail to send" className="ls-err">
                You have no inbox to send from yet.
              </Callout>
            ) : null}
            <FromRow
              mailboxes={data.mailboxes}
              value={mailboxId}
              invalid={!!invalid.from}
              onChange={(id) => {
                setMailboxId(id);
                clear('from');
              }}
            />
            <div className="cmp-row">
              <span className="rl">To</span>
              <span className="rv">
                <span className="rcpt ls-view">
                  <Icon name={listName ? 'list' : 'users'} />
                  {listName ?? `${plural(data.requested, 'selected person', 'selected people')}`}
                  <b className="tnum">{formatNumber(data.requested)}</b>
                </span>
              </span>
            </div>
            <div className={['cmp-row', invalid.subject ? 'invalid' : ''].filter(Boolean).join(' ')}>
              <label className="rl" htmlFor="ls-subj">
                Subject
              </label>
              <span className="rv">
                <input
                  ref={subjectRef}
                  id="ls-subj"
                  value={subject}
                  maxLength={400}
                  autoComplete="off"
                  placeholder="An idea for {company}"
                  aria-invalid={!!invalid.subject || undefined}
                  onChange={(e) => {
                    setSubject(e.target.value);
                    clear('subject');
                  }}
                />
              </span>
            </div>
            <TemplateRow templateId={templateId} onApply={applyTemplate} current={() => ({ subject, html })} onSaved={(t) => setTemplateId(t.id)} />

            <div className="ls-prev">
              <div className="ls-prevh">
                <Segmented
                  label="Message"
                  value={view}
                  onChange={setView}
                  options={[
                    { id: 'write', label: 'Write', icon: 'edit' },
                    { id: 'preview', label: 'Preview', icon: 'eye' },
                  ]}
                />
                {view === 'write' ? (
                  <span className="merge-fields" aria-label="Merge fields used">
                    {fields.map((f) => (
                      <span key={f} className="mfield">
                        {f}
                      </span>
                    ))}
                  </span>
                ) : person ? (
                  <span className="ls-pas" title={person.company ? `${person.name} · ${person.company}` : person.name}>
                    <span className="flabel">Preview as</span>
                    <Avatar name={person.name} hue={person.hue} size="xs" />
                    <b>{person.name}</b>
                    {person.company ? <span className="muted">· {person.company}</span> : null}
                  </span>
                ) : null}
                <span className="spacer" />
                {view === 'write' ? (
                  <MergeMenu values={{ first: values?.first ?? null, company: values?.company ?? null }} onInsert={(f) => editor.current?.insertText(f)} />
                ) : count > 1 ? (
                  <>
                    <button type="button" className="icon-btn xs" aria-label="Previous person" data-tip="Previous" onClick={() => setPi((i) => (i - 1 + count) % count)}>
                      <Icon name="chev-left" />
                    </button>
                    <span className="ls-pn" aria-live="polite">
                      {(pi % count) + 1} of {formatNumber(count)}
                    </span>
                    <button type="button" className="icon-btn xs" aria-label="Next person" data-tip="Next" onClick={() => setPi((i) => (i + 1) % count)}>
                      <Icon name="chev-right" />
                    </button>
                  </>
                ) : null}
              </div>
              {view === 'write' ? (
                <div className={['ls-write', invalid.body ? 'invalid' : ''].filter(Boolean).join(' ')}>
                  <FormatBar ref={fmt} editor={editor} />
                  <div className="cmp-body">
                    <Editor
                      ref={editor}
                      initialHtml={html}
                      invalid={!!invalid.body}
                      onLinkKey={() => fmt.current?.openLink()}
                      onChange={(v) => {
                        setHtml(v);
                        clear('body');
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="preview">
                  {values ? (
                    <>
                      <div className="ls-psubj">
                        <span className="flabel">Subject</span>
                        <b dangerouslySetInnerHTML={{ __html: subject.trim() ? highlightMergeFields(escapeHtml(subject), values) : '<span class="muted">No subject yet</span>' }} />
                      </div>
                      <div className="ls-pbody" dangerouslySetInnerHTML={{ __html: highlightMergeFields(cleanPastedHtml(html), values) }} />
                    </>
                  ) : (
                    <p className="muted">Nobody on this list can be emailed, so there is nothing to preview.</p>
                  )}
                  <div className="unsub-foot">
                    Prefer not to hear from us? <u>Unsubscribe</u>
                  </div>
                </div>
              )}
              <p className="field-help ls-help">
                {'{first}'} and {'{company}'} are filled in for each person. Every copy ends with an unsubscribe link.
              </p>
            </div>
          </div>

          <div className="ls-sum">
            <div className="ls-card">
              <div className="lh">
                <Icon name="users" />
                Recipients
              </div>
              <div className="ls-big tnum">
                {formatNumber(count)}
                <small>{count === 1 ? 'personal copy' : 'personal copies'}</small>
              </div>
              <div className="ls-sub">
                {formatNumber(data.requested)} selected · {formatNumber(data.skipped.length)} skipped automatically
              </div>
              {data.skipped.length ? (
                <SkippedList skipped={data.skipped} />
              ) : (
                <div className="ls-sub ls-ok">
                  <Icon name="check-circle" className="i-xs" /> Nobody needs skipping
                </div>
              )}
            </div>

            <div className="ls-card">
              <div className="lh">
                <Icon name="clock" />
                Pacing
              </div>
              {plan && count > 0 ? (
                <>
                  <div className="ls-pace">
                    <b>{count > 1 ? intervalLabel(plan.interval) : 'One email'}</b>
                    {count > 1 && plan.finish ? <span>{durationLabel(plan.finish.getTime() - plan.times[0]!.getTime())}</span> : null}
                  </div>
                  {bars.length ? (
                    <div className="pace" aria-hidden="true">
                      {bars.map((h, i) => (
                        <i key={i} style={{ height: `${h}%` }} />
                      ))}
                    </div>
                  ) : null}
                  <div className="ls-sub">{pacingNote(start, plan.finish, count)}</div>
                </>
              ) : (
                <div className="ls-sub">{mb ? 'Nobody to send to' : 'Pick an inbox to send from'}</div>
              )}
            </div>

            <div className={['ls-card', limit ? `lim-${limit.tone}` : ''].filter(Boolean).join(' ')}>
              <div className="lh">
                <Icon name="gauge" />
                {mb?.kind === 'personal' || mb?.kind === 'shared' ? 'Gmail daily limit' : 'Daily limit'}
              </div>
              {mb && limit ? (
                <>
                  <div
                    className="limit-meter"
                    role="img"
                    aria-label={`${formatNumber(used)} ${start ? 'sent in the 24 hours before' : 'used today'}, ${formatNumber(waiting)} already scheduled, ${formatNumber(count)} for this send, of ${formatNumber(mb.dailyLimit)}`}
                  >
                    <i style={{ width: pct(used), background: 'var(--muted)' }} />
                    <i style={{ width: pct(Math.min(waiting, Math.max(0, mb.dailyLimit - used))), background: 'var(--faint)' }} />
                    <i style={{ width: pct(Math.min(count, Math.max(0, mb.dailyLimit - used - waiting))), background: limit.tone === 'over' ? 'var(--warning)' : 'var(--accent)' }} />
                  </div>
                  <div className="ls-sub">{boldTotal(limit.text)}</div>
                </>
              ) : mb && room.isError ? (
                <>
                  <div className="ls-sub">The count for that time did not load.</div>
                  <button type="button" className="btn btn-sm btn-tile" onClick={() => void room.refetch()}>
                    <Icon name="refresh" />
                    Try again
                  </button>
                </>
              ) : mb ? (
                <div className="ls-sub">Counting what is already scheduled</div>
              ) : (
                <div className="ls-sub">Shown once you pick an inbox</div>
              )}
            </div>

            <Switch checked={trackOpens} onChange={setTrackOpens} className="ls-track">
              Track opens
              <span className="muted ls-hint">· shown as a hint</span>
            </Switch>
          </div>
        </div>
      </DialogBody>
      <DialogFoot>
        <div className="segc" role="group" aria-label="When to send">
          <button
            type="button"
            aria-pressed={when === 'now'}
            onClick={() => {
              setWhen('now');
              clear('startAt');
            }}
          >
            Send now
          </button>
          <button
            type="button"
            aria-pressed={when === 'later'}
            aria-haspopup="dialog"
            aria-expanded={timePop.open}
            className={invalid.startAt ? 'bad' : undefined}
            onClick={(e) => {
              setWhen('later');
              timePop.anchorProps.onClick(e);
            }}
          >
            <Icon name="calendar" className="i-xs" />
            {startAt ? formatDayTime(startAt) : 'Later'}
          </button>
        </div>
        <Popover state={timePop} width={340} maxWidth={340} role="dialog" label="When to start" align="left" className="ls-time">
          <PickTime
            initial={startAt ?? nextMorning()}
            action="Set time"
            onPick={(at) => {
              setStartAt(at);
              setWhen('later');
              clear('startAt');
              timePop.close();
            }}
          />
        </Popover>
        {invalid.startAt ? (
          <span className="note err" role="alert">
            <Icon name="warn" />
            {invalid.startAt}
          </span>
        ) : null}
        <span className="spacer" />
        <DialogCancel onClick={close} />
        <button type="button" className="btn btn-primary" onClick={submit} aria-busy={go.isPending || undefined} aria-disabled={go.isPending || count === 0 || undefined} data-key={`${MOD} ↵`}>
          <Icon name={when === 'later' ? 'clock' : 'send'} />
          {go.isPending ? 'Starting' : `${when === 'later' ? 'Schedule' : 'Send'} ${plural(count, 'email')}`}
        </button>
      </DialogFoot>
    </div>
  );
}

function SkippedList({ skipped }: { skipped: SkippedPerson[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? skipped : skipped.slice(0, 6);
  return (
    <div className="excl">
      {shown.map((x) => (
        <div key={x.personId} title={x.detail ?? undefined}>
          <Avatar name={x.name} hue={x.hue} size="xs" />
          <span className="grow">
            <b>{x.name}</b>
            {x.company ? <small>{x.company}</small> : null}
          </span>
          <Chip tone={SKIP_TONE[x.reason] ?? 'plain'}>{x.reason}</Chip>
        </div>
      ))}
      {skipped.length > shown.length ? (
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setAll(true)}>
          Show {plural(skipped.length - shown.length, 'more person', 'more people')}
        </button>
      ) : null}
    </div>
  );
}

/** 09:00 tomorrow, the usual first suggestion for a list send. */
function nextMorning(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}

/** Puts the total after "=" in bold, as the mock does. */
function boldTotal(text: string): ReactNode {
  const at = text.indexOf('= ');
  const end = at < 0 ? -1 : text.indexOf(' of ', at);
  if (end < 0) return text;
  return (
    <>
      {text.slice(0, at + 2)}
      <b>{text.slice(at + 2, end)}</b>
      {text.slice(end)}
    </>
  );
}
